import { useEffect, useRef } from 'react';

export default function VideoControlsEl({hdSrc, sdSrc, isAnimating = false}) {

    let src = hdSrc;
    if (window.innerWidth < 768) {
        src = sdSrc;
    }

  const playerRef = useRef(null);
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const playPauseBtnRef = useRef(null);
  const muteBtnRef = useRef(null);
  const volumeSliderRef = useRef(null);
  const volumeBgRef = useRef(null);
  const seekSliderRef = useRef(null);
  const seekBgRef = useRef(null);
  const fullBtnRef = useRef(null);
  const timestampRef = useRef(null);
  const durationRef = useRef(null);
  const progressRef = useRef(null);

useEffect(() => {
    const player = playerRef.current;
    const video = videoRef.current;

    if (!video || !player) return;

    if (isAnimating) {
      video.pause();
    }

    // Event handlers
    const playPause = () => {
      if (!video.paused && !video.ended) {
        video.pause();
      } else {
        video.play();
      }
    };

    const videoPlaying = () => {
      player.classList.add('playing');
      player.classList.remove('paused', 'ended');
      timestampRef.current?.classList.add('show');
    };

    const videoPaused = () => {
      player.classList.remove('playing');
      player.classList.add('paused');
    };

    const videoEnded = () => {
      video.currentTime = 0;
    };

    const videoTimeUpdate = () => {
      const rawTime = video.currentTime;
      const minutes = String(Math.floor(rawTime / 60)).padStart(2, '0');
      const seconds = String(Math.floor(rawTime % 60)).padStart(2, '0');
      const progress = (100 / video.duration) * video.currentTime;

      progressRef.current.innerText = `${minutes}:${seconds}`;
      timestampRef.current.style.left = `${progress}%`;
      seekSliderRef.current.value = progress;
      seekBgRef.current.style.width = `${progress}%`;
    };

    const videoSeek = () => {
      video.currentTime = (video.duration * seekSliderRef.current.value) / 100;
    };

    const volumeToggle = () => {
      video.muted = !video.muted;
      player.classList.toggle('muted', video.muted);
    };

    const volumeChange = () => {
      video.volume = volumeSliderRef.current.value;
      volumeBgRef.current.style.width = `${volumeSliderRef.current.value * 100}%`;
    };

    const goFullView = () => {
      //if (player.requestFullscreen) player.requestFullscreen();
      
        if (!video) return;

        if (video.requestFullscreen) {
            video.requestFullscreen();
        } else if (video.webkitEnterFullscreen) {
            video.webkitEnterFullscreen();
        } else {
            console.warn('Fullscreen API not supported');
        }

    };

    const handleKeyDown = (event) => {
    // Prevent spacebar from scrolling the page
    if (event.code === 'Space' || event.key === ' ') {
        event.preventDefault();
        playPause();
    }
    };

    document.addEventListener('keydown', handleKeyDown);

    // Add listeners
    video.addEventListener('click', playPause);
    video.addEventListener('playing', videoPlaying);
    video.addEventListener('pause', videoPaused);
    video.addEventListener('ended', videoEnded);
    video.addEventListener('timeupdate', videoTimeUpdate);

    playPauseBtnRef.current.addEventListener('click', playPause);
    muteBtnRef.current.addEventListener('click', volumeToggle);
    volumeSliderRef.current.addEventListener('input', volumeChange);
    seekSliderRef.current.addEventListener('input', videoSeek);
    fullBtnRef.current.addEventListener('click', goFullView);

    // Mouse move hide controls
    let mouseTimeout;
    const showControls = () => {
      clearTimeout(mouseTimeout);
      if (!controlsRef.current) return;
      controlsRef.current.classList.remove('hide');
      mouseTimeout = setTimeout(() => {
        if (controlsRef.current) {
          controlsRef.current.classList.add('hide');
        }
      }, 2000);
    };

    const hideControls = () => {
      if (controlsRef.current) {
        controlsRef.current.classList.add('hide');
      }
    };

    player.addEventListener('mousemove', showControls);
    player.addEventListener('mouseleave', () => controlsRef.current.classList.add('hide'));
    showControls();

    // Cleanup
    return () => {

      clearTimeout(mouseTimeout);

      video.removeEventListener('click', playPause);
      video.removeEventListener('playing', videoPlaying);
      video.removeEventListener('pause', videoPaused);
      video.removeEventListener('ended', videoEnded);
      video.removeEventListener('timeupdate', videoTimeUpdate);

      document.removeEventListener('keydown', handleKeyDown);

      playPauseBtnRef.current?.removeEventListener('click', playPause);
        muteBtnRef.current?.removeEventListener('click', volumeToggle);
        volumeSliderRef.current?.removeEventListener('input', volumeChange);
        seekSliderRef.current?.removeEventListener('input', videoSeek);
        fullBtnRef.current?.removeEventListener('click', goFullView);

        player?.removeEventListener('mousemove', showControls);
        player?.removeEventListener('mouseleave', () => controlsRef.current?.classList.add('hide'));
    };
  }, [isAnimating]);

    return (

        <div ref={playerRef} className="fullvideo">
            <div className="video_wrapper">
              <video autoPlay playsInline ref={videoRef}>
                  <source src={src} />
              </video>
              <div className="controls" ref={controlsRef}>
                  <div className="controlPanel">
                      <div className="playpause hoverme" id="playpause" ref={playPauseBtnRef}>
                          <svg className="play" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 27 31.17">
                              <g id="Layer_2" data-name="Layer 2">
                                  <g id="initial_State" data-name="initial State">
                                      <polygon className="cls-1" points="27 15.59 0 0 0 31.17 27 15.59" />
                                  </g>
                              </g>
                          </svg>
                          <svg className="pause" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24.3 31.26">
                              <g id="Layer_2" data-name="Layer 2">
                                  <g id="Playing_State" data-name="Playing State">
                                      <rect className="cls-1" x="-11.29" y="11.29" width="31.26" height="8.68" transform="translate(-11.29 19.97) rotate(-90)" />
                                      <rect className="cls-1" x="4.33" y="11.29" width="31.26" height="8.68" transform="translate(4.33 35.58) rotate(-90)" />
                                  </g>
                              </g>
                          </svg>
                          <svg className="replay" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25.97 26.57">
                              <g id="Layer_2" data-name="Layer 2">
                                  <g id="End_State" data-name="End State">
                                      <polyline className="cls-1" points="9.76 8.07 1.78 8.07 1.78 0" />
                                      <path className="cls-1" d="M2.81,7.8A11.44,11.44,0,1,1,1.7,16.16" />
                                  </g>
                              </g>
                          </svg>
                      </div>

                      <div className="scrubber hoverme">
                          <input type="range" ref={seekSliderRef} id="seek-bar" defaultValue="0" />
                          <div className="bg" ref={seekBgRef}></div>
                          <div className="timestamp">
                              <span className="progress" ref={progressRef}>0:00</span>
                              <span className="duration" ref={timestampRef}>0:00</span>
                          </div>
                      </div>

                      <div className="volume">
                          <svg className="mute hoverme" ref={muteBtnRef} xmlns="http://www.w3.org/2000/svg" width="11.816" height="17.269" viewBox="0 0 11.816 17.269">
                              <path id="Icon_feather-volume" data-name="Icon feather-volume" d="M12.816,7.5,7.363,11.863H3v6.544H7.363l5.453,4.363Z" transform="translate(-2 -6.5)" fill="none" stroke="#fff" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                          </svg>
                          <div className="volumewrap">
                              <input type="range" ref={volumeSliderRef} id="volume-bar" min="0" max="1" step="0.1" defaultValue="1" />
                              <div className="bg" ref={volumeBgRef}></div>
                          </div>
                      </div>
                      <div className="fullscreen">
                          <svg className="gofull" ref={fullBtnRef} xmlns="http://www.w3.org/2000/svg" width="25.43" height="25.43" viewBox="0 0 25.43 25.43">
                              <g id="Group_98" data-name="Group 98" transform="translate(2 2)">
                                  <g id="Group_96" data-name="Group 96" transform="translate(15.496 9.563) rotate(45)">
                                      <g id="Group_95" data-name="Group 95" transform="translate(0)">
                                          <line id="Line_38" data-name="Line 38" x2="8.391" transform="translate(0 4.195)" fill="none" stroke="#fff" strokeLinecap="round" strokeWidth="2" />
                                          <line id="Line_39" data-name="Line 39" x2="4.195" y2="4.195" transform="translate(4.195 0)" fill="none" stroke="#fff" strokeLinecap="round" strokeWidth="2" />
                                          <line id="Line_40" data-name="Line 40" y1="4.195" x2="4.195" transform="translate(4.195 4.195)" fill="none" stroke="#fff" strokeLinecap="round" strokeWidth="2" />
                                      </g>
                                  </g>
                                  <g id="Group_97" data-name="Group 97" transform="translate(5.933 11.866) rotate(-135)">
                                      <g id="Group_95-2" data-name="Group 95" transform="translate(0)">
                                          <path id="Path_68" data-name="Path 68" d="M0,0H8.391" transform="translate(0 4.195)" fill="none" stroke="#fff" strokeLinecap="round" strokeWidth="2" />
                                          <line id="Line_39-2" data-name="Line 39" x2="4.195" y2="4.195" transform="translate(4.195 0)" fill="none" stroke="#fff" strokeLinecap="round" strokeWidth="2" />
                                          <line id="Line_40-2" data-name="Line 40" y1="4.195" x2="4.195" transform="translate(4.195 4.195)" fill="none" stroke="#fff" strokeLinecap="round" strokeWidth="2" />
                                      </g>
                                  </g>
                              </g>
                          </svg>
                      </div>
                  </div>
              </div>
            </div>
        </div>

    )

}